import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { cameras, Camera } from '../../data/cameras';
import { Incident } from '../../services/supabase';

// Mapbox token
const MAPBOX_TOKEN = 'pk.eyJ1IjoiZGFiMSIsImEiOiJjbDZmeGw3bm0wYTN1M2JtamV6cW1jZWVqIn0.e-0qo3T9hooEEc2rnoo9Ag';

// Verify token is set
if (!MAPBOX_TOKEN) {
  throw new Error('Mapbox token is required');
}

// Set the token globally
mapboxgl.accessToken = MAPBOX_TOKEN;

export interface MapRef {
  flyToIncident: (coordinates: [number, number], incident: Incident) => void;
  flyToCamera: (lat: number, lon: number) => void;
}

interface MapProps {
  onCameraClick?: (camera: Camera) => void;
  incidents?: Incident[];
  showHeatmap?: boolean;
}

// Define GeoJSON types
interface CameraProperties extends Camera {
  name: string;
  camera_count: number;
  video_url?: string;
}

interface CameraFeature {
  type: 'Feature';
  properties: CameraProperties;
  geometry: {
    type: 'Point';
    coordinates: [number, number];
  };
}

interface CameraCollection {
  type: 'FeatureCollection';
  features: CameraFeature[];
}

interface IncidentFeature {
  type: 'Feature';
  properties: {
    id: string;
    category: string;
    description: string;
    datetime: string;
    priority: string;
    intersection: string;
  };
  geometry: {
    type: 'Point';
    coordinates: [number, number];
  };
}

interface IncidentCollection {
  type: 'FeatureCollection';
  features: IncidentFeature[];
}

const Map = forwardRef<MapRef, MapProps>(({ onCameraClick, incidents = [], showHeatmap = false }, ref) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const animationFrameRef = useRef<number | undefined>(undefined);
  const activePopupRef = useRef<mapboxgl.Popup | null>(null);
  const activeIncidentPlaybackRef = useRef<{ popup: mapboxgl.Popup; cleanup: () => void } | null>(null);
  const [animationComplete, setAnimationComplete] = useState(false);
  const [lng] = useState(-122.4194);
  const [lat] = useState(37.7749);
  const [zoom] = useState(12);

  // Helper function to get priority color
  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'a': return '#ef4444';
      case 'b': return '#f59e0b';
      case 'c': return '#eab308';
      default: return '#6b7280';
    }
  };

  // Add helper function for relative time
  const getRelativeTimeString = (date: Date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    const days = Math.floor(diffInSeconds / 86400);
    const hours = Math.floor((diffInSeconds % 86400) / 3600);
    const minutes = Math.floor((diffInSeconds % 3600) / 60);

    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);

    return parts.length > 0 ? parts.join(' ') + ' ago' : 'just now';
  };

  // Keep incidents source in sync with props
  useEffect(() => {
    if (!map.current) return;
    const m = map.current;
    const src = m.getSource('incidents') as mapboxgl.GeoJSONSource | undefined;
    if (!src) return;
    const data = {
      type: 'FeatureCollection',
      features: (incidents || []).map(incident => {
        const point: any = incident.intersection_point;
        if (!point) return null;
        let coordinates: [number, number] | null = null;
        if ('coordinates' in point && Array.isArray(point.coordinates)) {
          coordinates = point.coordinates as [number, number];
        } else if ('latitude' in point && 'longitude' in point) {
          coordinates = [point.longitude, point.latitude];
        }
        if (!coordinates) return null;
        return {
          type: 'Feature',
          properties: {
            id: incident.id,
            category: incident.incident_category,
            description: incident.incident_description,
            datetime: incident.incident_datetime,
            priority: incident.priority,
            intersection: incident.intersection
          },
          geometry: { type: 'Point', coordinates }
        } as IncidentFeature;
      }).filter(Boolean) as IncidentFeature[]
    } as IncidentCollection;
    try { src.setData(data as any); } catch { }
  }, [incidents]);

  // Toggle heatmap vs markers based on prop
  useEffect(() => {
    if (!map.current) return;
    const m = map.current;
    try {
      if (m.getLayer('incidents-heatmap')) m.setLayoutProperty('incidents-heatmap', 'visibility', showHeatmap ? 'visible' : 'none');
      if (m.getLayer('incidents-circles')) m.setLayoutProperty('incidents-circles', 'visibility', showHeatmap ? 'none' : 'visible');
      if (m.getLayer('incidents-labels')) m.setLayoutProperty('incidents-labels', 'visibility', showHeatmap ? 'none' : 'visible');
    } catch { }
  }, [showHeatmap]);

  // Update showPopup function
  const showPopup = (coordinates: [number, number], incident: Incident) => {
    if (activePopupRef.current) {
      activePopupRef.current.remove();
    }

    const priorityColor = getPriorityColor(incident.priority);
    const incidentDate = new Date(incident.incident_datetime);
    const relativeTime = incident.id === 'hardcoded-1' ? '' : getRelativeTimeString(incidentDate);

    // Create the popup HTML content first
    const popupContent = document.createElement('div');
    popupContent.innerHTML = `
      <div class="orchestra-popup-header" style="background: ${priorityColor}">
        <div class="orchestra-popup-priority priority-${incident.priority.toLowerCase()}">${incident.priority}</div>
        <div class="orchestra-popup-category">${incident.incident_category}</div>
        <div class="orchestra-popup-location">
          <svg class="orchestra-popup-location-icon" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z"/>
          </svg>
          ${incident.intersection}
        </div>
      </div>
      <div class="orchestra-popup-body">
        <div class="orchestra-popup-time">
          <svg class="orchestra-popup-time-icon" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20ZM12.5 7H11V13L16.2 16.2L17 14.9L12.5 12.2V7Z"/>
          </svg>
          <span class="orchestra-popup-time-text">
            ${incidentDate.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    })}
          </span>
        </div>
        ${relativeTime ? `
        <div class="orchestra-popup-relative-time">
          <svg class="orchestra-popup-time-icon" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M13 3C8.03 3 4 7.03 4 12H1L4.89 15.89L4.96 16.03L9 12H6C6 8.13 9.13 5 13 5C16.87 5 20 8.13 20 12C20 15.87 16.87 19 13 19C11.07 19 9.32 18.21 8.06 16.94L6.64 18.36C8.27 19.99 10.51 21 13 21C17.97 21 22 16.97 22 12C22 7.03 17.97 3 13 3Z"/>
          </svg>
          <span class="orchestra-popup-time-text">${relativeTime}</span>
        </div>
        ` : ''}
        ${incident.incident_description ? `
          <div class="orchestra-popup-description">
            ${incident.incident_description}
          </div>
        ` : ''}
      </div>
    `;

    // Create the popup
    const popup = new mapboxgl.Popup({
      className: 'orchestra-popup',
      closeButton: true,
      closeOnClick: false,
      maxWidth: 'none',
      anchor: 'bottom',
      offset: [0, -10]
    })
      .setLngLat(coordinates)
      .setDOMContent(popupContent);

    // Add to map
    popup.addTo(map.current!);

    activePopupRef.current = popup;

    popup.on('close', () => {
      activePopupRef.current = null;
    });
  };

  // Expose the flyToIncident method via ref
  useImperativeHandle(ref, () => ({
    flyToIncident: (coordinates: [number, number], incident: Incident) => {
      if (!map.current) return;

      map.current.flyTo({
        center: coordinates,
        zoom: 16,
        duration: 2000,
        essential: true
      });

      // Show popup after flying
      setTimeout(() => {
        if (!map.current) return;
        showPopup(coordinates, incident);
      }, 2000);
    },
    flyToCamera: (lat: number, lon: number) => {
      if (map.current) {
        map.current.flyTo({
          center: [lon, lat],
          zoom: 18,
          pitch: 60,
          bearing: Math.random() * 360, // Random bearing for dramatic effect
          duration: 2000, // 2 seconds animation
          essential: true
        });
      }
    }
  }));

  // Create custom camera icon
  const createCameraIcon = () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.error('Could not get canvas context');
      return { width: 64, height: 64, data: new Uint8Array(64 * 64 * 4) };
    }

    const size = 64;
    canvas.width = size;
    canvas.height = size;

    // Clear canvas
    ctx.clearRect(0, 0, size, size);

    // Outer glow
    const glowGradient = ctx.createRadialGradient(size / 2, size / 2, size / 6, size / 2, size / 2, size / 2);
    glowGradient.addColorStop(0, 'rgba(0, 162, 255, 0.4)');
    glowGradient.addColorStop(0.5, 'rgba(0, 162, 255, 0.1)');
    glowGradient.addColorStop(1, 'rgba(0, 162, 255, 0)');

    ctx.fillStyle = glowGradient;
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
    ctx.fill();

    // Main circle background
    ctx.fillStyle = 'rgba(15, 23, 42, 0.95)';
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 4, 0, Math.PI * 2);
    ctx.fill();

    // Inner ring
    ctx.strokeStyle = 'rgba(0, 162, 255, 0.8)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 4 - 2, 0, Math.PI * 2);
    ctx.stroke();

    // Camera lens outer ring
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 6, 0, Math.PI * 2);
    ctx.stroke();

    // Camera lens
    const lensGradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 6);
    lensGradient.addColorStop(0, 'rgba(0, 162, 255, 0.2)');
    lensGradient.addColorStop(0.7, 'rgba(0, 162, 255, 0.1)');
    lensGradient.addColorStop(1, 'rgba(0, 162, 255, 0.05)');

    ctx.fillStyle = lensGradient;
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 6, 0, Math.PI * 2);
    ctx.fill();

    // Add subtle highlight
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(size / 2, size / 2 - 2, size / 8, 0, Math.PI);
    ctx.stroke();

    return {
      width: size,
      height: size,
      data: ctx.getImageData(0, 0, size, size).data
    };
  };

  // Update setupPulseAnimation for smoother animation
  const setupPulseAnimation = (mapInstance: mapboxgl.Map) => {
    let pulseStep = 0;
    const PULSE_STEPS = 60; // Increased for smoother animation

    const animatePulse = (map: mapboxgl.Map) => {
      pulseStep = (pulseStep + 1) % PULSE_STEPS;
      const pulseFactor = Math.sin((pulseStep / PULSE_STEPS) * Math.PI * 2);
      const scale = 0.5 + pulseFactor * 0.05; // Subtle scale animation

      map.setLayoutProperty('unclustered-cameras', 'icon-size', scale);

      animationFrameRef.current = requestAnimationFrame(() => animatePulse(map));
    };

    animatePulse(mapInstance);
  };

  // Update styles
  const addStyles = () => {
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      .orchestra-popup {
        border-radius: 12px;
        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
      }

      .orchestra-popup .mapboxgl-popup-content {
        background: rgba(25, 25, 28, 0.95);
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        border-radius: 12px;
        padding: 0;
        overflow: hidden;
        width: 340px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
        border: 1px solid rgba(255, 255, 255, 0.1);
      }

      .orchestra-popup .mapboxgl-popup-tip {
        display: none;
      }

      .orchestra-popup .mapboxgl-popup-close-button {
        width: 28px;
        height: 28px;
        padding: 0;
        color: rgba(255, 255, 255, 0.7);
        font-size: 20px;
        background: rgba(0, 0, 0, 0.2);
        border-radius: 8px;
        border: 1px solid rgba(255, 255, 255, 0.1);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.2s ease;
        z-index: 3;
        top: 16px;
        right: 16px;
        backdrop-filter: blur(4px);
        -webkit-backdrop-filter: blur(4px);
      }

      .orchestra-popup .mapboxgl-popup-close-button:hover {
        color: rgba(255, 255, 255, 1);
        background: rgba(255, 255, 255, 0.1);
        transform: scale(1.05);
      }

      .orchestra-popup-header {
        position: relative;
        padding: 20px;
        background: linear-gradient(to bottom, rgba(0, 0, 0, 0.2), transparent);
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      }

      .orchestra-popup-category {
        font-size: 18px;
        font-weight: 600;
        color: rgba(255, 255, 255, 0.95);
        margin-bottom: 8px;
        line-height: 1.3;
        padding-right: 32px;
        letter-spacing: -0.3px;
      }

      .orchestra-popup-location {
        font-size: 13px;
        color: rgba(255, 255, 255, 0.7);
        display: flex;
        align-items: center;
        gap: 6px;
        margin-top: 12px;
      }

      .orchestra-popup-location-icon {
        width: 16px;
        height: 16px;
        opacity: 0.7;
      }

      .orchestra-popup-body {
        padding: 20px;
      }

      .orchestra-popup-priority {
        position: absolute;
        top: 20px;
        right: 56px;
        font-size: 13px;
        font-weight: 600;
        padding: 6px 10px;
        border-radius: 6px;
        background: rgba(0, 0, 0, 0.2);
        color: rgba(255, 255, 255, 0.95);
        letter-spacing: 0.5px;
        backdrop-filter: blur(4px);
        -webkit-backdrop-filter: blur(4px);
        border: 1px solid rgba(255, 255, 255, 0.1);
      }

      .orchestra-popup-time,
      .orchestra-popup-relative-time {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px 12px;
        background: rgba(255, 255, 255, 0.05);
        border-radius: 8px;
        margin-bottom: 8px;
        border: 1px solid rgba(255, 255, 255, 0.05);
        transition: all 0.2s ease;
      }

      .orchestra-popup-time:hover,
      .orchestra-popup-relative-time:hover {
        background: rgba(255, 255, 255, 0.08);
        border-color: rgba(255, 255, 255, 0.1);
      }

      .orchestra-popup-time-icon {
        width: 16px;
        height: 16px;
        opacity: 0.6;
      }

      .orchestra-popup-time-text {
        font-size: 13px;
        color: rgba(255, 255, 255, 0.8);
        font-weight: 500;
      }

      .orchestra-popup-description {
        font-size: 13px;
        line-height: 1.6;
        color: rgba(255, 255, 255, 0.8);
        background: rgba(255, 255, 255, 0.05);
        border-radius: 8px;
        padding: 12px 14px;
        margin-top: 16px;
        border: 1px solid rgba(255, 255, 255, 0.05);
        transition: all 0.2s ease;
      }

      .orchestra-popup-description:hover {
        background: rgba(255, 255, 255, 0.08);
        border-color: rgba(255, 255, 255, 0.1);
      }

      @media (max-width: 768px) {
        .orchestra-popup .mapboxgl-popup-content {
          width: 300px;
        }

        .orchestra-popup-header {
          padding: 16px;
        }

        .orchestra-popup-body {
          padding: 16px;
        }
      }

      /* Camera popup specific styles */
      .camera-popup .orchestra-popup-header {
        padding: 20px;
        background: linear-gradient(180deg, rgba(0, 162, 255, 0.15) 0%, rgba(0, 162, 255, 0.05) 100%);
        border-bottom: 1px solid rgba(0, 162, 255, 0.1);
      }

      .camera-type-badge {
        display: inline-block;
        padding: 4px 8px;
        background: rgba(0, 162, 255, 0.15);
        border: 1px solid rgba(0, 162, 255, 0.2);
        border-radius: 4px;
        color: rgba(0, 162, 255, 0.9);
        font-size: 12px;
        font-weight: 500;
        margin-bottom: 12px;
      }

      .camera-location {
        font-size: 18px;
        font-weight: 600;
        color: rgba(255, 255, 255, 0.95);
        margin: 0 0 12px 0;
        line-height: 1.3;
      }

      .camera-count {
        display: flex;
        align-items: center;
        gap: 8px;
        color: rgba(255, 255, 255, 0.7);
        font-size: 13px;
        margin-bottom: 12px;
      }

      .camera-count svg {
        opacity: 0.7;
      }

      .camera-status {
        display: flex;
        align-items: center;
        gap: 8px;
        color: rgba(255, 255, 255, 0.7);
        font-size: 13px;
      }

      .status-dot {
        width: 8px;
        height: 8px;
        background: #10B981;
        border-radius: 50%;
        box-shadow: 0 0 8px rgba(16, 185, 129, 0.4);
      }

      .camera-body {
        padding: 16px;
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .camera-action-button {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px 12px;
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 6px;
        color: rgba(255, 255, 255, 0.8);
        text-decoration: none;
        font-size: 13px;
        font-weight: 500;
        transition: all 0.2s ease;
      }

      .camera-action-button:hover {
        background: rgba(255, 255, 255, 0.1);
        border-color: rgba(255, 255, 255, 0.2);
        transform: translateY(-1px);
      }

      .camera-action-button.primary {
        background: rgba(0, 162, 255, 0.15);
        border-color: rgba(0, 162, 255, 0.2);
        color: rgba(0, 162, 255, 0.9);
      }

      .camera-action-button.primary:hover {
        background: rgba(0, 162, 255, 0.2);
        border-color: rgba(0, 162, 255, 0.3);
      }

      .camera-action-button svg {
        opacity: 0.8;
      }

      .camera-feed-container {
        position: relative;
        width: 100%;
        height: 0;
        padding-bottom: 56.25%; /* 16:9 aspect ratio */
        background: rgba(0, 0, 0, 0.2);
        border-radius: 8px;
        overflow: hidden;
        border: 1px solid rgba(255, 255, 255, 0.1);
      }

      .camera-feed {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: #000;
      }

      /* Update popup max width for video */
      .camera-popup.has-feed .mapboxgl-popup-content {
        width: 480px;
        max-width: 90vw;
      }

      /* Responsive adjustments */
      @media (max-width: 768px) {
        .camera-popup.has-feed .mapboxgl-popup-content {
          width: 100vw;
          max-width: 100vw;
          border-radius: 12px 12px 0 0;
          position: fixed;
          bottom: 0;
          left: 0;
        }

        .camera-popup.has-feed {
          position: fixed !important;
          bottom: 0 !important;
          left: 0 !important;
          transform: none !important;
        }

        .camera-feed-container {
          border-radius: 8px 8px 0 0;
        }
      }
    `;
    document.head.appendChild(styleElement);
  };

  useEffect(() => {
    if (!mapContainer.current) {
      console.error('Map container not found');
      return;
    }

    if (map.current) {
      console.log('Map already initialized');
      return;
    }

    try {
      const mapInstance = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/dark-v11',
        center: [-122.4194, 37.7749],
        zoom: 12,
        pitch: 45,
        bearing: 15,
        antialias: true
      });

      // Add navigation controls
      mapInstance.addControl(new mapboxgl.NavigationControl(), 'bottom-right');

      map.current = mapInstance;

      // Add styles
      addStyles();

      // Wait for map to load before adding features
      mapInstance.on('load', () => {
        console.log('Map loaded successfully');

        // Add hardcoded incident
        const hardcodedIncident: Incident = {
          id: 'hardcoded-1',
          incident_category: 'Car Robbery',
          incident_description: '',
          incident_datetime: '2025-04-01T01:57:59',
          report_datetime: '2025-04-01T01:57:59',
          priority: 'A',
          intersection: 'Grant Ave \\ Green St',
          intersection_point: {
            type: 'Point',
            coordinates: [-122.40738600140656, 37.799617937789805]
          }
        };

        // Add hardcoded incident source
        // mapInstance.addSource('hardcoded-incident', {
        //   type: 'geojson',
        //   data: {
        //     type: 'Feature',
        //     properties: {
        //       id: hardcodedIncident.id,
        //       category: 'CAR ROBBERY',  // Ensure it's uppercase in the source
        //       description: hardcodedIncident.incident_description,
        //       datetime: hardcodedIncident.incident_datetime,
        //       priority: hardcodedIncident.priority,
        //       intersection: hardcodedIncident.intersection
        //     },
        //     geometry: {
        //       type: 'Point',
        //       coordinates: [-122.40738600140656, 37.799617937789805]
        //     }
        //   }
        // });

        // Add hardcoded incident layer (matching existing incidents style)
        // mapInstance.addLayer({
        //   id: 'hardcoded-incident',
        //   type: 'circle',
        //   source: 'hardcoded-incident',
        //   paint: {
        //     'circle-radius': [
        //       'interpolate',
        //       ['linear'],
        //       ['zoom'],
        //       10, 4,
        //       15, 8
        //     ],
        //     'circle-color': '#ff0000',
        //     'circle-opacity': 0.8,
        //     'circle-stroke-width': 1,
        //     'circle-stroke-color': '#000000'
        //   }
        // });

        // Add label layer for hardcoded incident (matching existing labels style)
        // mapInstance.addLayer({
        //   id: 'hardcoded-incident-label',
        //   type: 'symbol',
        //   source: 'hardcoded-incident',
        //   layout: {
        //     'text-field': ['get', 'category'],
        //     'text-size': 12,
        //     'text-offset': [0, 1.5],
        //     'text-anchor': 'top'
        //   },
        //   paint: {
        //     'text-color': '#ffffff',
        //     'text-halo-color': '#000000',
        //     'text-halo-width': 1
        //   },
        //   minzoom: 13
        // });

        // Add click handler for hardcoded incident
        // mapInstance.on('click', 'hardcoded-incident', (e) => {
        //   if (!e.features?.length) return;

        //   const coordinates = (e.features[0].geometry as any).coordinates.slice();
        //   showPopup(coordinates, hardcodedIncident);
        // });

        // Change cursor on hover
        mapInstance.on('mouseenter', 'hardcoded-incident', () => {
          mapInstance.getCanvas().style.cursor = 'pointer';
        });

        mapInstance.on('mouseleave', 'hardcoded-incident', () => {
          mapInstance.getCanvas().style.cursor = '';
        });

        // Add fog effect
        mapInstance.setFog({
          'color': 'rgba(186, 210, 235, 0.2)',
          'high-color': 'rgba(36, 92, 223, 0.2)',
          'horizon-blend': 0.02,
          'space-color': '#000000',
          'star-intensity': 0.6
        });

        // Add 3D buildings
        mapInstance.addLayer({
          'id': '3d-buildings',
          'source': 'composite',
          'source-layer': 'building',
          'type': 'fill-extrusion',
          'minzoom': 15,
          'paint': {
            'fill-extrusion-color': '#aaa',
            'fill-extrusion-height': [
              'interpolate', ['linear'], ['zoom'],
              15, 0,
              16, ['get', 'height']
            ],
            'fill-extrusion-base': ['get', 'min_height'],
            'fill-extrusion-opacity': 0.6
          }
        });

        // Add incidents source and layer
        mapInstance.addSource('incidents', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: incidents.map(incident => {
              const point = incident.intersection_point;
              if (!point) return null;

              // Extract coordinates based on the point format
              let coordinates: [number, number] | null = null;

              if ('coordinates' in point && Array.isArray(point.coordinates)) {
                coordinates = point.coordinates as [number, number];
              } else if ('latitude' in point && 'longitude' in point) {
                coordinates = [point.longitude, point.latitude];
              }

              if (!coordinates) return null;

              return {
                type: 'Feature',
                properties: {
                  id: incident.id,
                  category: incident.incident_category,
                  description: incident.incident_description,
                  datetime: incident.incident_datetime,
                  priority: incident.priority,
                  intersection: incident.intersection
                },
                geometry: {
                  type: 'Point',
                  coordinates
                }
              };
            }).filter(Boolean) as IncidentFeature[]
          }
        });

        // Add incident heatmap layer (below circles by default)
        mapInstance.addLayer({
          id: 'incidents-heatmap',
          type: 'heatmap',
          source: 'incidents',
          maxzoom: 18,
          paint: {
            'heatmap-radius': [
              'interpolate', ['linear'], ['zoom'],
              10, 10,
              14, 20,
              18, 30
            ],
            'heatmap-opacity': [
              'interpolate', ['linear'], ['zoom'],
              10, 0.7,
              18, 0.9
            ],
            'heatmap-weight': 1,
            'heatmap-intensity': [
              'interpolate', ['linear'], ['zoom'],
              10, 0.6,
              18, 1.2
            ],
            'heatmap-color': [
              'interpolate', ['linear'], ['heatmap-density'],
              0, 'rgba(0,0,0,0)',
              0.2, 'rgba(0,255,255,0.5)',
              0.4, 'rgba(0,191,255,0.6)',
              0.6, 'rgba(0,127,255,0.7)',
              0.8, 'rgba(127,0,127,0.9)',
              1, 'rgba(255,0,0,1)'
            ]
          }
        });

        // Add incident circles layer
        mapInstance.addLayer({
          id: 'incidents-circles',
          type: 'circle',
          source: 'incidents',
          paint: {
            'circle-radius': [
              'interpolate',
              ['linear'],
              ['zoom'],
              10, 4,
              15, 8
            ],
            'circle-color': [
              'match',
              ['get', 'priority'],
              'A', '#ff0000',
              'B', '#ff6b00',
              'C', '#ffd700',
              '#808080'
            ],
            'circle-opacity': 0.8,
            'circle-stroke-width': 1,
            'circle-stroke-color': '#000000'
          }
        });

        // Add incident labels layer
        mapInstance.addLayer({
          id: 'incidents-labels',
          type: 'symbol',
          source: 'incidents',
          layout: {
            'text-field': ['get', 'category'],
            'text-size': 12,
            'text-offset': [0, 1.5],
            'text-anchor': 'top'
          },
          paint: {
            'text-color': '#ffffff',
            'text-halo-color': '#000000',
            'text-halo-width': 1
          },
          minzoom: 13
        });

        // Initialize visibility based on heatmap toggle
        try {
          mapInstance.setLayoutProperty('incidents-heatmap', 'visibility', showHeatmap ? 'visible' : 'none');
          mapInstance.setLayoutProperty('incidents-circles', 'visibility', showHeatmap ? 'none' : 'visible');
          mapInstance.setLayoutProperty('incidents-labels', 'visibility', showHeatmap ? 'none' : 'visible');
        } catch { }

        // Add popup for incidents
        mapInstance.on('click', 'incidents-circles', (e) => {
          if (!e.features?.length) return;

          const feature = e.features[0];
          const coordinates = (feature.geometry as any).coordinates.slice();
          const properties = feature.properties;

          if (!properties) return;

          const incident: Incident = {
            id: properties.id,
            incident_category: properties.category,
            incident_description: properties.description,
            incident_datetime: properties.datetime,
            report_datetime: properties.datetime,
            intersection: properties.intersection,
            priority: properties.priority,
            intersection_point: {
              type: 'Point',
              coordinates: coordinates
            }
          };

          showPopup(coordinates, incident);

          // Time-matched playback function (only invoked when status check confirms video within ±10 min)
          // For initial MVP, we target a known camera with NAS mapping.
          const openIncidentMatchedPlayback = async (
            cameraId: string,
            whenIso: string,
            anchor: [number, number]
          ) => {
            if (cameraId !== '32') return;

            const when = new Date(whenIso);
            const whenEpoch = Math.floor(when.getTime() / 1000);
            const pad = (n: number) => String(n).padStart(2, '0');
            const y = when.getFullYear();
            const m = pad(when.getMonth() + 1);
            const d = pad(when.getDate());
            const dateStr = `${y}-${m}-${d}`; // folder name on NAS

            // Ensure only one incident playback popup at a time
            if (activeIncidentPlaybackRef.current) {
              try { activeIncidentPlaybackRef.current.cleanup(); } catch { }
              try { activeIncidentPlaybackRef.current.popup.remove(); } catch { }
              activeIncidentPlaybackRef.current = null;
            }

            // UI popup container
            const popupEl = document.createElement('div');
            popupEl.innerHTML = `
              <div class="orchestra-popup-header camera-header">
                <div class="camera-type-badge">Incident Playback</div>
                <h3 class="camera-location">${incident.incident_category}</h3>
                <div class="camera-status">
                  <span class="status-dot"></span>
                  <span>Camera 32 • ${dateStr}</span>
                </div>
              </div>
              <div class="orchestra-popup-body camera-body">
                <div style="font-size:13px;color:rgba(255,255,255,0.7);margin-bottom:8px;">Matching ${when.toLocaleString()}</div>
                <div class="camera-feed-container">
                  <div id="incpb-loading" style="position:absolute; inset:0; display:flex; align-items:center; justify-content:center; background:rgba(0,0,0,0.35); color:#fff; font-size:12px;">Loading…</div>
                  <video id="incpb-video" class="camera-feed" controls playsinline></video>
                </div>
              </div>
            `;

            const popup = new mapboxgl.Popup({
              className: 'orchestra-popup camera-popup has-feed',
              closeButton: true,
              closeOnClick: false,
              maxWidth: '480px',
              anchor: 'bottom',
              offset: [0, -10]
            })
              .setLngLat(anchor)
              .setDOMContent(popupEl)
              .addTo(mapInstance);

            const loadHlsJs = (): Promise<any> => {
              return new Promise((resolve, reject) => {
                if ((window as any).Hls) return resolve((window as any).Hls);
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/hls.js@latest';
                script.onload = () => resolve((window as any).Hls);
                script.onerror = reject;
                document.body.appendChild(script);
              });
            };

            const parseStart = (name: string): Date | null => {
              // Example: cam01_2025-08-11_15-29-01.mkv
              const m = name.match(/_(\d{4}-\d{2}-\d{2})_(\d{2})-(\d{2})-(\d{2})\.mkv$/);
              if (!m) return null;
              const ymd = m[1].split('-').map(Number);
              const hh = Number(m[2]);
              const mm = Number(m[3]);
              const ss = Number(m[4]);
              return new Date(ymd[0], ymd[1] - 1, ymd[2], hh, mm, ss);
            };

            const loadingEl = popupEl.querySelector('#incpb-loading') as HTMLDivElement | null;
            const videoEl = popupEl.querySelector('#incpb-video') as HTMLVideoElement | null;
            let hlsInstance: any | null = null;

            const cleanup = () => {
              if (hlsInstance) {
                try { hlsInstance.destroy(); } catch { }
                hlsInstance = null;
              }
              if (videoEl) {
                try { videoEl.pause(); videoEl.removeAttribute('src'); videoEl.load(); } catch { }
              }
              activeIncidentPlaybackRef.current = null;
            };

            try {
              // Prefer precise meta (startEpoch) when available
              let chosenName: string | null = null;
              let chosenStartEpoch: number = 0;
              try {
                const metaResp = await fetch(`http://localhost:8000/api/playback/segments_meta/32?date=${encodeURIComponent(dateStr)}`);
                if (metaResp.ok) {
                  const meta = await metaResp.json();
                  const segs: { name: string; durationSeconds?: number; startEpoch?: number }[] = meta.segments || [];
                  if (segs.length > 0 && typeof segs[0].startEpoch === 'number') {
                    const ordered = segs
                      .filter(s => typeof s.startEpoch === 'number')
                      .sort((a, b) => (a.startEpoch! - b.startEpoch!));
                    // If no files within ±10 minutes, report no recording
                    const nearestDelta = ordered.reduce((acc, s) => Math.min(acc, Math.abs(whenEpoch - (s.startEpoch || 0))), Number.POSITIVE_INFINITY);
                    if (nearestDelta > 600) {
                      if (loadingEl) loadingEl.textContent = 'No recording around this incident (±10 min).';
                      // Track this popup as active for proper cleanup
                      activeIncidentPlaybackRef.current = { popup, cleanup };
                      return;
                    }
                    let idx = 0;
                    for (let i = 0; i < ordered.length; i++) {
                      const cur = ordered[i];
                      const next = i < ordered.length - 1 ? ordered[i + 1] : null;
                      const curStart = cur.startEpoch!;
                      const nextStart = next ? next.startEpoch! : Number.POSITIVE_INFINITY;
                      if (whenEpoch >= curStart && whenEpoch < nextStart) { idx = i; break; }
                      if (whenEpoch < curStart) { idx = Math.max(0, i - 1); break; }
                      idx = i;
                    }
                    chosenName = ordered[idx].name;
                    chosenStartEpoch = ordered[idx].startEpoch!;
                  }
                }
              } catch { }

              // Fallback to filename parsing if meta missing
              if (!chosenName) {
                const resp = await fetch(`http://localhost:8000/api/playback/segments/32?date=${encodeURIComponent(dateStr)}`);
                if (!resp.ok) throw new Error('Failed to list segments');
                const data = await resp.json();
                const names: string[] = data.segments || [];
                if (!names.length) throw new Error('No video segments for date');
                const withStarts = names
                  .map(n => ({ name: n, start: parseStart(n) }))
                  .filter(x => !!x.start)
                  .sort((a, b) => (a.start!.getTime() - b.start!.getTime())) as { name: string; start: Date }[];
                const nearestDelta = withStarts.reduce((acc, s) => Math.min(acc, Math.abs(whenEpoch - Math.floor(s.start.getTime() / 1000))), Number.POSITIVE_INFINITY);
                if (nearestDelta > 600) {
                  if (loadingEl) loadingEl.textContent = 'No recording around this incident (±10 min).';
                  activeIncidentPlaybackRef.current = { popup, cleanup };
                  return;
                }
                let chosen = withStarts[0];
                for (let i = 0; i < withStarts.length; i++) {
                  const cur = withStarts[i];
                  const next = i < withStarts.length - 1 ? withStarts[i + 1] : null;
                  const curTs = Math.floor(cur.start.getTime() / 1000);
                  const nextTs = next ? Math.floor(next.start.getTime() / 1000) : Number.POSITIVE_INFINITY;
                  if (whenEpoch >= curTs && whenEpoch < nextTs) { chosen = cur; break; }
                  if (whenEpoch < curTs) { chosen = i > 0 ? withStarts[i - 1] : cur; break; }
                }
                chosenName = chosen.name;
                chosenStartEpoch = Math.floor(chosen.start.getTime() / 1000);
              }

              const offsetSec = Math.max(0, whenEpoch - chosenStartEpoch);

              // Request HLS VOD manifest for the chosen file
              const fileParam = `${dateStr}/${chosenName}`;
              const hlsResp = await fetch(`http://localhost:8000/api/playback/hls/32?file=${encodeURIComponent(fileParam)}`);
              if (!hlsResp.ok) throw new Error('Failed to prepare HLS');
              const hlsData = await hlsResp.json();
              const m3u8Path = hlsData.m3u8 as string; // e.g., /hls_vod/<hash>/index.m3u8
              const m3u8Url = `http://localhost:8000${m3u8Path}`;

              if (!videoEl) throw new Error('Video element missing');
              if (videoEl.canPlayType('application/vnd.apple.mpegURL')) {
                videoEl.src = m3u8Url;
              } else {
                const Hls = await loadHlsJs();
                if (Hls && Hls.isSupported()) {
                  hlsInstance = new Hls();
                  hlsInstance.loadSource(m3u8Url);
                  hlsInstance.attachMedia(videoEl);
                } else {
                  // Fallback to direct file stream (may not be browser-compatible)
                  videoEl.src = `http://localhost:8000/api/playback/stream/32?file=${encodeURIComponent(fileParam)}`;
                }
              }

              const onLoaded = () => {
                try { videoEl.currentTime = offsetSec; } catch { }
                videoEl.play().catch(() => { });
                videoEl.removeEventListener('loadedmetadata', onLoaded);
                if (loadingEl) loadingEl.style.display = 'none';
              };
              if (loadingEl) loadingEl.style.display = 'flex';
              videoEl.addEventListener('loadedmetadata', onLoaded);
              // Track this popup as active for proper cleanup
              activeIncidentPlaybackRef.current = { popup, cleanup };
            } catch (err) {
              if (loadingEl) loadingEl.textContent = 'No playback available at incident time.';
              console.error('Incident matched playback failed:', err);
            }

            popup.on('close', cleanup);
          };

          // Quick status check: Only load playback if we have a segment within ±10 minutes
          (async () => {
            const when = new Date(incident.incident_datetime);
            const whenEpoch = Math.floor(when.getTime() / 1000);
            const pad = (n: number) => String(n).padStart(2, '0');
            const y = when.getFullYear();
            const m = pad(when.getMonth() + 1);
            const d = pad(when.getDate());
            const dateStr = `${y}-${m}-${d}`;
            const popupBody = activePopupRef.current?.getElement()?.querySelector('.orchestra-popup-body') as HTMLDivElement | null;
            const statusEl = document.createElement('div');
            statusEl.style.marginTop = '12px';
            statusEl.style.color = 'rgba(255,255,255,0.7)';
            statusEl.style.fontSize = '13px';
            statusEl.textContent = 'Checking for nearby footage…';
            popupBody?.appendChild(statusEl);

            const withinWindow = async (): Promise<boolean> => {
              try {
                const metaResp = await fetch(`http://localhost:8000/api/playback/segments_meta/32?date=${encodeURIComponent(dateStr)}`);
                if (metaResp.ok) {
                  const meta = await metaResp.json();
                  const segs: { startEpoch?: number }[] = meta.segments || [];
                  const deltas = segs
                    .filter(s => typeof s.startEpoch === 'number')
                    .map(s => Math.abs((s.startEpoch as number) - whenEpoch));
                  if (deltas.length) return Math.min(...deltas) <= 600; // 10 minutes
                }
                // fallback to plain listing if meta not available
                const resp = await fetch(`http://localhost:8000/api/playback/segments/32?date=${encodeURIComponent(dateStr)}`);
                if (!resp.ok) return false;
                const data = await resp.json();
                const names: string[] = data.segments || [];
                const nearest = names.reduce((acc, n) => {
                  const m = n.match(/_(\d{4}-\d{2}-\d{2})_(\d{2})-(\d{2})-(\d{2})\.mkv$/);
                  if (!m) return acc;
                  const [_, ymd, hh, mm, ss] = m;
                  const [yy, mo, dd] = ymd.split('-').map(Number);
                  const dt = new Date(yy, mo - 1, dd, Number(hh), Number(mm), Number(ss));
                  const delta = Math.abs(Math.floor(dt.getTime() / 1000) - whenEpoch);
                  return Math.min(acc, delta);
                }, Number.POSITIVE_INFINITY);
                return nearest <= 600;
              } catch { return false; }
            };

            if (await withinWindow()) {
              statusEl.textContent = 'Video evidence found for this time. Loading…';
              openIncidentMatchedPlayback('32', incident.incident_datetime, coordinates as [number, number]);
            } else {
              // statusEl.textContent = 'No video evidence found within ±10 minutes.';
            }
          })();
        });

        // Change cursor on hover
        mapInstance.on('mouseenter', 'incidents-circles', () => {
          mapInstance.getCanvas().style.cursor = 'pointer';
        });

        mapInstance.on('mouseleave', 'incidents-circles', () => {
          mapInstance.getCanvas().style.cursor = '';
        });

        // Group cameras by coordinates
        const coordinateGroups: { [key: string]: Camera[] } = {};
        cameras.forEach(camera => {
          const coordKey = `${camera.lon}-${camera.lat}`;
          if (!coordinateGroups[coordKey]) {
            coordinateGroups[coordKey] = [];
          }
          coordinateGroups[coordKey].push(camera);
        });

        // Create camera features with jittering for duplicates
        const cameraFeatures: CameraFeature[] = Object.values(coordinateGroups).flatMap(cameraGroup => {
          if (cameraGroup.length === 1) {
            return [{
              type: 'Feature',
              properties: {
                ...cameraGroup[0],
                name: cameraGroup[0].location,
                camera_count: 1
              },
              geometry: {
                type: 'Point',
                coordinates: [Number(cameraGroup[0].lon), Number(cameraGroup[0].lat)]
              }
            }];
          }

          // Apply jittering for multiple cameras
          return cameraGroup.map((camera, index) => {
            const angle = (index / cameraGroup.length) * Math.PI * 2;
            const jitterAmount = 0.0002;
            const jitteredLon = Number(camera.lon) + Math.cos(angle) * jitterAmount;
            const jitteredLat = Number(camera.lat) + Math.sin(angle) * jitterAmount;

            return {
              type: 'Feature',
              properties: {
                ...camera,
                name: camera.location,
                camera_count: cameraGroup.length
              },
              geometry: {
                type: 'Point',
                coordinates: [jitteredLon, jitteredLat]
              }
            };
          });
        });

        // Add camera source with clustering
        mapInstance.addSource('camera-points', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: cameraFeatures
          } as CameraCollection,
          // cluster: true,
          clusterMaxZoom: 14,
          clusterRadius: 50
        });

        // Add custom icons
        mapInstance.addImage('camera-icon', createCameraIcon());

        // Add camera layers
        mapInstance.addLayer({
          id: 'unclustered-cameras',
          type: 'symbol',
          source: 'camera-points',
          filter: ['!', ['has', 'point_count']],
          layout: {
            'icon-image': 'camera-icon',
            'icon-size': 0.5,
            'icon-allow-overlap': true,
            'icon-ignore-placement': true
          }
        });

        // Add cluster layers
        mapInstance.addLayer({
          id: 'clusters',
          type: 'circle',
          source: 'camera-points',
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': [
              'step',
              ['get', 'point_count'],
              'rgba(0, 162, 255, 0.3)',  // Base color
              10, 'rgba(0, 162, 255, 0.4)',  // Medium clusters
              25, 'rgba(0, 162, 255, 0.5)'   // Large clusters
            ],
            'circle-radius': [
              'step',
              ['get', 'point_count'],
              20,  // Base size
              10, 30,  // Medium clusters
              25, 40   // Large clusters
            ],
            'circle-stroke-width': 2,
            'circle-stroke-color': 'rgba(0, 162, 255, 0.8)',
            'circle-stroke-opacity': 0.8
          }
        });

        // Add cluster count
        mapInstance.addLayer({
          id: 'cluster-count',
          type: 'symbol',
          source: 'camera-points',
          filter: ['has', 'point_count'],
          layout: {
            'text-field': '{point_count_abbreviated}',
            'text-font': ['DIN Pro Medium', 'Arial Unicode MS Bold'],
            'text-size': 14
          },
          paint: {
            'text-color': '#ffffff',
            'text-halo-color': 'rgba(0, 0, 0, 0.5)',
            'text-halo-width': 2
          }
        });

        // Handle cluster click
        mapInstance.on('click', 'clusters', (e) => {
          const features = mapInstance.queryRenderedFeatures(e.point, { layers: ['clusters'] });
          if (!features.length) return;

          const clusterId = features[0].properties?.cluster_id;
          if (!clusterId) return;

          const source = mapInstance.getSource('camera-points');
          if (!source || !('getClusterExpansionZoom' in source)) return;

          (source as mapboxgl.GeoJSONSource).getClusterExpansionZoom(
            clusterId,
            (error, result) => {
              if (error || result === null) return;

              mapInstance.easeTo({
                center: (features[0].geometry as any).coordinates,
                zoom: result
              });
            }
          );
        });

        // Add hover effects
        mapInstance.on('mouseenter', 'clusters', () => {
          mapInstance.getCanvas().style.cursor = 'pointer';
        });

        mapInstance.on('mouseleave', 'clusters', () => {
          mapInstance.getCanvas().style.cursor = '';
        });

        // Add popup for individual cameras
        mapInstance.on('click', 'unclustered-cameras', (e) => {
          if (!e.features?.length) return;

          const feature = e.features[0];
          const coordinates = (feature.geometry as any).coordinates.slice();
          const properties = feature.properties;

          if (!properties) return;

          // Special handling: For camera id 32, start custom HLS ingest and provide Live/Playback tabs
          if (properties.camera_id === '32') {
            const popupEl = document.createElement('div');
            popupEl.innerHTML = `
              <div class="orchestra-popup-header camera-header">
                <div class="camera-type-badge">Camera</div>
                <h3 class="camera-location">${properties.name}</h3>
                <div class="camera-status">
                  <span class="status-dot"></span>
                  <span>Live & Playback</span>
                </div>
              </div>
              <div class="orchestra-popup-body camera-body">
                <div style="display:flex; gap:8px; margin-bottom:8px;">
                  <button id="tab-live" class="camera-action-button primary">Live</button>
                  <button id="tab-playback" class="camera-action-button">Playback</button>
                </div>
                <div id="live-pane">
                  <div class="camera-feed-container">
                    <video id="camera32-video" class="camera-feed" controls autoplay muted playsinline></video>
                  </div>
                </div>
                <div id="playback-pane" style="display:none;">
                  <div style="display:flex; gap:8px; align-items:center; margin-bottom:8px;">
                    <input id="pb-date" type="date" style="background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); color: #fff; padding: 6px 8px; border-radius: 6px;" />
                  </div>
                  <div id="pb-status" style="display:none;"></div>
                  <div id="pb-prep" style="height:6px; width:100%; background:rgba(255,255,255,0.08); border-radius:4px; overflow:hidden; display:none; margin-bottom:10px;">
                    <div id="pb-prep-bar" style="height:100%; width:0%; background:#3B82F6;"></div>
                  </div>
                  <div style="display:flex; align-items:center; gap:8px; margin: 6px 0 12px 0;">
                    <button id="pb-prev" class="camera-action-button">Prev</button>
                    <input id="pb-timeline" type="range" min="0" max="0" value="0" step="1" style="flex:1;" />
                    <button id="pb-next" class="camera-action-button">Next</button>
                    <div id="pb-time-label" style="width:90px; text-align:right; color:#9ca3af; font-size:12px;">0:00 / 0:00</div>
                  </div>
                  <div class="camera-feed-container" style="margin-top:8px;">
                    <div id="pb-loading" style="position:absolute; inset:0; display:none; align-items:center; justify-content:center; background:rgba(0,0,0,0.35); color:#fff; font-size:12px;">
                      Loading…
                    </div>
                    <video id="pb-video" class="camera-feed" controls playsinline></video>
                  </div>
                </div>
              </div>
            `;

            const popup = new mapboxgl.Popup({
              className: 'orchestra-popup camera-popup has-feed',
              closeButton: true,
              closeOnClick: false,
              maxWidth: '480px',
              anchor: 'bottom',
              offset: [0, -10]
            })
              .setLngLat(coordinates)
              .setDOMContent(popupEl)
              .addTo(mapInstance);

            // Helper to load hls.js from CDN if needed
            const loadHlsJs = (): Promise<any> => {
              return new Promise((resolve, reject) => {
                if ((window as any).Hls) return resolve((window as any).Hls);
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/hls.js@latest';
                script.onload = () => resolve((window as any).Hls);
                script.onerror = reject;
                document.body.appendChild(script);
              });
            };

            // Route via backend proxy to avoid dev proxy 405s on POST
            const startUrl = 'http://localhost:8000/api/hls/start/cam01';
            const stopUrl = 'http://localhost:8000/api/hls/stop/cam01';
            const streamUrl = '/hls/cam01/index.m3u8';

            let hlsInstance: any | null = null;
            let pbHlsInstance: any | null = null;
            const videoEl = popupEl.querySelector('#camera32-video') as HTMLVideoElement | null;
            const pbVideoEl = popupEl.querySelector('#pb-video') as HTMLVideoElement | null;

            // Start ingest then attach player
            (async () => {
              try {
                // Fire and forget start; ignore CORS preflight failures here
                fetch(startUrl, { method: 'POST', mode: 'cors' }).catch(() => { });

                if (!videoEl) return;
                // If browser supports native HLS (Safari), just set src
                if (videoEl.canPlayType('application/vnd.apple.mpegURL')) {
                  videoEl.src = streamUrl;
                } else {
                  const Hls = await loadHlsJs();
                  if (Hls && Hls.isSupported()) {
                    hlsInstance = new Hls();
                    hlsInstance.loadSource(streamUrl);
                    hlsInstance.attachMedia(videoEl);
                  } else {
                    // Fallback attempt
                    videoEl.src = streamUrl;
                  }
                }
              } catch (err) {
                console.error('Failed to start or attach HLS stream:', err);
              }
            })();

            // Stop ingest on popup close and cleanup
            popup.on('close', () => {
              try {
                fetch(stopUrl, { method: 'POST', mode: 'cors' }).catch(() => { });
              } catch { }
              if (hlsInstance) {
                try { hlsInstance.destroy(); } catch { }
                hlsInstance = null;
              }
              if (pbHlsInstance) {
                try { pbHlsInstance.destroy(); } catch { }
                pbHlsInstance = null;
              }
              if (videoEl) {
                try { videoEl.pause(); videoEl.removeAttribute('src'); videoEl.load(); } catch { }
              }
              if (pbVideoEl) {
                try { pbVideoEl.pause(); pbVideoEl.removeAttribute('src'); pbVideoEl.load(); } catch { }
              }
            });

            // Tabs and playback wiring
            const liveTab = popupEl.querySelector('#tab-live') as HTMLButtonElement | null;
            const pbTab = popupEl.querySelector('#tab-playback') as HTMLButtonElement | null;
            const livePane = popupEl.querySelector('#live-pane') as HTMLDivElement | null;
            const pbPane = popupEl.querySelector('#playback-pane') as HTMLDivElement | null;

            const setActiveTab = (which: 'live' | 'pb') => {
              if (!liveTab || !pbTab || !livePane || !pbPane) return;
              if (which === 'live') {
                liveTab.classList.add('primary');
                pbTab.classList.remove('primary');
                livePane.style.display = '';
                pbPane.style.display = 'none';
              } else {
                pbTab.classList.add('primary');
                liveTab.classList.remove('primary');
                pbPane.style.display = '';
                livePane.style.display = 'none';
              }
            };
            liveTab?.addEventListener('click', () => setActiveTab('live'));
            pbTab?.addEventListener('click', () => {
              setActiveTab('pb');
              const loadingEl = popupEl.querySelector('#pb-loading') as HTMLDivElement | null;
              if (loadingEl) loadingEl.style.display = 'flex';
              if (pbVideoEl) pbVideoEl.style.visibility = 'hidden';
              // Lazy-load segments when entering Playback tab
              if (pbDateInput?.value) listSegments(pbDateInput.value);
            });
            // Kick off background prefetch shortly after popup opens (defer to ensure declarations exist)
            setTimeout(() => {
              const dateEl = popupEl.querySelector('#pb-date') as HTMLInputElement | null;
              if (dateEl && dateEl.value) prefetchForDate(dateEl.value);
            }, 0);

            const pbDateInput = popupEl.querySelector('#pb-date') as HTMLInputElement | null;
            const pbList = null as unknown as HTMLDivElement | null;
            const pbTimeline = popupEl.querySelector('#pb-timeline') as HTMLInputElement | null;
            const pbTimeLabel = popupEl.querySelector('#pb-time-label') as HTMLDivElement | null;
            const pbPrevBtn = popupEl.querySelector('#pb-prev') as HTMLButtonElement | null;
            const pbNextBtn = popupEl.querySelector('#pb-next') as HTMLButtonElement | null;
            const pbStatus = popupEl.querySelector('#pb-status') as HTMLDivElement | null;
            const pbPrep = popupEl.querySelector('#pb-prep') as HTMLDivElement | null;
            const pbPrepBar = popupEl.querySelector('#pb-prep-bar') as HTMLDivElement | null;

            type SegmentMeta = { name: string; durationSeconds: number; start: number };
            let segmentsMeta: SegmentMeta[] = [];
            let initialBatchSize = 5; // start with 5 files
            let loadedCount = 0;
            let totalDuration = 0;
            const segmentsCache: Record<string, SegmentMeta[]> = {};
            let currentIndex = -1;

            const fmtTime = (s: number) => {
              const m = Math.floor(s / 60);
              const ss = Math.floor(s % 60);
              return `${m}:${String(ss).padStart(2, '0')}`;
            };

            const updateTimeLabel = () => {
              const current = pbTimeline ? Number(pbTimeline.value) : 0;
              if (pbTimeLabel) pbTimeLabel.textContent = `${fmtTime(current)} / ${fmtTime(totalDuration)}`;
            };

            const findIndexForTime = (t: number) => {
              for (let i = 0; i < segmentsMeta.length; i++) {
                const seg = segmentsMeta[i];
                const nextStart = i < segmentsMeta.length - 1 ? segmentsMeta[i + 1].start : totalDuration;
                if (t >= seg.start && t < nextStart) return i;
              }
              return segmentsMeta.length - 1;
            };

            const playSegmentAtIndex = async (index: number, seekGlobalTime?: number) => {
              if (!pbVideoEl || index < 0 || index >= segmentsMeta.length) return;
              currentIndex = index;
              const dateFolder = pbDateInput?.value || '';
              const seg = segmentsMeta[index];
              const fileParam = `${dateFolder}/${seg.name}`;
              const playUrl = `http://localhost:8000/api/playback/stream/32?file=${encodeURIComponent(fileParam)}`;
              const desiredTimeInSeg = typeof seekGlobalTime === 'number' ? Math.max(0, seekGlobalTime - seg.start) : 0;
              const loadingEl = popupEl.querySelector('#pb-loading') as HTMLDivElement | null;
              if (loadingEl) loadingEl.style.display = 'flex';
              pbVideoEl.style.visibility = 'hidden';
              pbVideoEl.src = playUrl;
              const onLoaded = () => {
                pbVideoEl.currentTime = desiredTimeInSeg;
                pbVideoEl.play().catch(() => { });
                pbVideoEl.removeEventListener('loadedmetadata', onLoaded);
                if (loadingEl) loadingEl.style.display = 'none';
                pbVideoEl.style.visibility = 'visible';
              };
              pbVideoEl.addEventListener('loadedmetadata', onLoaded);
            };

            // Pre-fill date with today in yyyy-mm-dd
            if (pbDateInput) {
              const d = new Date();
              const yyyy = d.getFullYear();
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const dd = String(d.getDate()).padStart(2, '0');
              pbDateInput.value = `${yyyy}-${mm}-${dd}`;
            }

            // Background prefetch while Live plays
            const prefetchForDate = async (dateStr: string) => {
              try {
                if (segmentsCache[dateStr]) return;
                console.log('[Playback] Preparing playback…');
                if (pbPrep) pbPrep.style.display = 'block';
                if (pbPrepBar) pbPrepBar.style.width = '0%';
                let resp = await fetch(`http://localhost:8000/api/playback/segments_meta/32?date=${encodeURIComponent(dateStr)}`);
                let segs: { name: string; durationSeconds: number }[] = [];
                if (resp.ok) {
                  const data = await resp.json();
                  segs = data.segments || [];
                } else {
                  resp = await fetch(`http://localhost:8000/api/playback/segments/32?date=${encodeURIComponent(dateStr)}`);
                  if (!resp.ok) return;
                  const data2 = await resp.json();
                  const names: string[] = data2.segments || [];
                  segs = names.map((n: string) => ({ name: n, durationSeconds: 60 }));
                }
                let start = 0;
                const metas: SegmentMeta[] = [];
                segs.forEach((s) => {
                  const dur = Math.max(1, Math.round(s.durationSeconds || 0));
                  metas.push({ name: s.name, durationSeconds: dur, start });
                  start += dur;
                });
                segmentsCache[dateStr] = metas;
                const warmCount = Math.min(5, metas.length);
                for (let i = 0; i < warmCount; i++) {
                  const fileParam = `${dateStr}/${metas[i].name}`;
                  const warmUrl = `http://localhost:8000/api/playback/stream/32?file=${encodeURIComponent(fileParam)}`;
                  try {
                    await fetch(warmUrl, { headers: { Range: 'bytes=0-131071' } });
                    if (pbPrepBar) pbPrepBar.style.width = `${Math.round(((i + 1) / warmCount) * 100)}%`;
                  } catch { }
                }
                console.log('[Playback] Playback ready');
                if (pbPrep) pbPrep.style.display = 'none';
              } catch { }
            };

            const listSegments = async (dateStr: string) => {
              try {
                let segsMeta: SegmentMeta[] | null = segmentsCache[dateStr] || null;
                if (!segsMeta) {
                  let resp = await fetch(`http://localhost:8000/api/playback/segments_meta/32?date=${encodeURIComponent(dateStr)}`);
                  let segs: { name: string; durationSeconds: number }[] = [];
                  if (resp.ok) {
                    const data = await resp.json();
                    segs = data.segments || [];
                  } else {
                    resp = await fetch(`http://localhost:8000/api/playback/segments/32?date=${encodeURIComponent(dateStr)}`);
                    if (!resp.ok) throw new Error('segments fetch failed');
                    const data2 = await resp.json();
                    const names: string[] = data2.segments || [];
                    segs = names.map((n: string) => ({ name: n, durationSeconds: 60 }));
                  }
                  let start = 0;
                  segsMeta = segs.map((s) => {
                    const dur = Math.max(1, Math.round(s.durationSeconds || 0));
                    const meta = { name: s.name, durationSeconds: dur, start } as SegmentMeta;
                    start += dur;
                    return meta;
                  });
                  segmentsCache[dateStr] = segsMeta;
                }
                segmentsMeta = [];
                totalDuration = 0;
                loadedCount = 0;
                if (!segsMeta || segsMeta.length === 0) {
                  if (pbTimeline) { pbTimeline.max = '0'; pbTimeline.value = '0'; }
                  totalDuration = 0;
                  updateTimeLabel();
                  return;
                }
                segmentsMeta = segsMeta.slice();
                totalDuration = segmentsMeta[segmentsMeta.length - 1].start + segmentsMeta[segmentsMeta.length - 1].durationSeconds;
                if (pbTimeline) { pbTimeline.max = String(totalDuration); pbTimeline.value = '0'; }
                updateTimeLabel();
                // Auto-start playback at beginning using only initial batch window
                const startIndex = 0;
                const endIndex = Math.min(segmentsMeta.length - 1, initialBatchSize - 1);
                loadedCount = endIndex + 1;
                playSegmentAtIndex(startIndex, 0);
              } catch (e) {
                // Ignore in UI
              }
            };

            // Auto-load segments on tab activation and on date change
            pbDateInput?.addEventListener('change', () => {
              if (pbDateInput?.value) listSegments(pbDateInput.value);
            });

            // Timeline scrubbing
            pbTimeline?.addEventListener('input', () => { updateTimeLabel(); });
            pbTimeline?.addEventListener('change', () => {
              if (!pbTimeline) return;
              const t = Number(pbTimeline.value);
              const idx = findIndexForTime(t);
              playSegmentAtIndex(idx, t);
            });

            // Prev/Next clip controls
            pbPrevBtn?.addEventListener('click', () => {
              if (currentIndex > 0) {
                playSegmentAtIndex(currentIndex - 1, segmentsMeta[currentIndex - 1].start);
                if (pbTimeline) pbTimeline.value = String(segmentsMeta[currentIndex - 1].start);
                updateTimeLabel();
              }
            });
            pbNextBtn?.addEventListener('click', () => {
              if (currentIndex >= 0 && currentIndex < segmentsMeta.length - 1) {
                playSegmentAtIndex(currentIndex + 1, segmentsMeta[currentIndex + 1].start);
                if (pbTimeline) pbTimeline.value = String(segmentsMeta[currentIndex + 1].start);
                updateTimeLabel();
              }
            });

            // Auto-advance to next segment and progressively extend window
            pbVideoEl?.addEventListener('ended', () => {
              if (currentIndex >= 0 && currentIndex < segmentsMeta.length - 1) {
                const nextIndex = currentIndex + 1;
                const nextStart = segmentsMeta[nextIndex].start;
                // If we are about to move beyond loadedCount, extend the window by 5 more segments
                if (nextIndex >= loadedCount) {
                  loadedCount = Math.min(segmentsMeta.length, loadedCount + 5);
                }
                playSegmentAtIndex(nextIndex, nextStart);
                if (pbTimeline) pbTimeline.value = String(nextStart);
                updateTimeLabel();
              }
            });

            return; // Do not proceed with default behavior
          }

          // Default behavior for other cameras
          new mapboxgl.Popup({
            className: 'orchestra-popup camera-popup',
            closeButton: true,
            closeOnClick: false,
            maxWidth: properties.iframeUrl ? '480px' : 'none',
            anchor: 'bottom',
            offset: [0, -10]
          })
            .setLngLat(coordinates)
            .setHTML(`
              <div class="orchestra-popup-header camera-header">

                <h3 class="camera-location">${properties.name}</h3>
                ${properties.camera_count > 1 ? `
                  <div class="camera-count">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 6H19.725L17.292 2.784C17.1006 2.53224 16.8516 2.33292 16.5657 2.20147C16.2798 2.07002 15.9654 2.01027 15.649 2.027L8.351 2.027C8.03458 2.01027 7.72021 2.07002 7.43428 2.20147C7.14835 2.33292 6.89937 2.53224 6.708 2.784L4.275 6H3C2.46957 6 1.96086 6.21071 1.58579 6.58579C1.21071 6.96086 1 7.46957 1 8V18C1 18.5304 1.21071 19.0391 1.58579 19.4142C1.96086 19.7893 2.46957 20 3 20H21C21.5304 20 22.0391 19.7893 22.4142 19.4142C22.7893 19.0391 23 18.5304 23 18V8C23 7.46957 22.7893 6.96086 22.4142 6.58579C22.0391 6.21071 21.5304 6 21 6ZM9.191 3.727C9.19148 3.72672 9.19198 3.72644 9.19249 3.72616C9.19941 3.71695 9.20761 3.70898 9.217 3.702C9.22639 3.69502 9.23692 3.68937 9.248 3.685C9.25934 3.68047 9.27113 3.67724 9.283 3.675H14.717C14.7289 3.67724 14.7407 3.68047 14.752 3.685C14.7631 3.68937 14.7736 3.69502 14.783 3.702C14.7924 3.70898 14.8006 3.71695 14.8075 3.72616C14.808 3.72644 14.8085 3.72672 14.809 3.727L16.559 6H7.441L9.191 3.727ZM21 18H3V8H21V18Z" fill="currentColor"/>
                      <path d="M12 7C10.8133 7 9.65327 7.35189 8.66658 8.01118C7.67988 8.67047 6.91085 9.60754 6.45672 10.7039C6.0026 11.8003 5.88378 13.0067 6.11529 14.1705C6.3468 15.3344 6.91825 16.4035 7.75736 17.2426C8.59648 18.0818 9.66558 18.6532 10.8295 18.8847C11.9933 19.1162 13.1997 18.9974 14.2961 18.5433C15.3925 18.0892 16.3295 17.3201 16.9888 16.3334C17.6481 15.3467 18 14.1867 18 13C18 11.4087 17.3679 9.88258 16.2426 8.75736C15.1174 7.63214 13.5913 7 12 7ZM12 17C11.0717 17 10.1666 16.7241 9.40825 16.2067C8.64991 15.6893 8.06266 14.9518 7.72134 14.0758C7.38002 13.1999 7.29832 12.2333 7.48822 11.3023C7.67812 10.3713 8.12796 9.51187 8.78769 8.85215C9.44741 8.19243 10.3069 7.74259 11.2379 7.55269C12.1689 7.36279 13.1355 7.44449 14.0114 7.78581C14.8874 8.12713 15.6249 8.71438 16.1423 9.47272C16.6597 10.2311 16.9355 11.1361 16.9355 12.0645C16.9355 13.3105 16.4388 14.5055 15.5557 15.3886C14.6727 16.2716 13.4776 16.7683 12.2316 16.7683L12 17Z" fill="currentColor"/>
                      <path d="M12 9C11.4067 9 10.8266 9.17595 10.3333 9.50559C9.83994 9.83524 9.45542 10.3038 9.22836 10.8519C9.0013 11.4001 8.94189 12.0033 9.05764 12.5853C9.17339 13.1672 9.45912 13.7018 9.87868 14.1213C10.2982 14.5409 10.8328 14.8266 11.4147 14.9424C11.9967 15.0581 12.5999 14.9987 13.1481 14.7716C13.6962 14.5446 14.1648 14.1601 14.4944 13.6667C14.8241 13.1734 15 12.5933 15 12C15 11.2044 14.6839 10.4413 14.1213 9.87868C13.5587 9.31607 12.7956 9 12 9ZM12 13C11.8022 13 11.6089 12.9414 11.4444 12.8315C11.28 12.7216 11.1518 12.5654 11.0761 12.3827C11.0004 12.2 10.9806 11.9989 11.0192 11.8049C11.0578 11.6109 11.153 11.4327 11.2929 11.2929C11.4327 11.153 11.6109 11.0578 11.8049 11.0192C11.9989 10.9806 12.2 11.0004 12.3827 11.0761C12.5654 11.1518 12.7216 11.28 12.8315 11.4444C12.9414 11.6089 13 11.8022 13 12C13 12.2652 12.8946 12.5196 12.7071 12.7071C12.5196 12.8946 12.2652 13 12 13Z" fill="currentColor"/>
                    </svg>
                    <span>${properties.camera_count} cameras</span>
                  </div>
                ` : ''}
                <div class="camera-status">
                  <span class="status-dot"></span>
                  <span>${properties.iframeUrl ? 'Live Feed Available' : 'Street View Available'}</span>
                </div>
              </div>
              <div class="orchestra-popup-body camera-body">
                ${properties.iframeUrl ? `
                  <div class="camera-feed-container">
                    <iframe 
                      src="${properties.iframeUrl}" 
                      frameborder="0" 
                      allowfullscreen
                      class="camera-feed"
                    ></iframe>
                  </div>
                ` : `
                  <a href="https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${coordinates[1]},${coordinates[0]}" 
                     target="_blank" 
                     class="camera-action-button">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21.6 10.2L21 9.6C20.85 9.45 20.625 9.375 20.4 9.375C20.175 9.375 19.95 9.45 19.8 9.6L18.6 10.8L19.8 12L21 10.8C21.3 10.5 21.3 10.05 21.6 9.75V9.6V10.2ZM17.4 12L14.4 9L3.6 19.8V22.8H6.6L17.4 12ZM3.075 3.075C2.475 3.675 2.475 4.725 3.075 5.325L6.225 8.475L9.375 5.325L8.475 4.425L9.225 3.675C9.825 3.075 9.825 2.025 9.225 1.425C8.625 0.825 7.575 0.825 6.975 1.425L6.225 2.175L5.325 1.275L3.075 3.075Z" fill="currentColor"/>
                    </svg>
                    View Street View
                  </a>
                `}
              </div>
            `)
            .addTo(mapInstance);

          if (onCameraClick) {
            const camera: Camera = {
              camera_id: properties.camera_id,
              location: properties.location,
              lat: properties.lat,
              lon: properties.lon,
              iframeUrl: properties.iframeUrl,
              iframeWidth: properties.iframeWidth,
              iframeHeight: properties.iframeHeight
            };
            onCameraClick(camera);
          }
        });

        // Just change cursor on hover
        mapInstance.on('mouseenter', 'unclustered-cameras', () => {
          mapInstance.getCanvas().style.cursor = 'pointer';
        });

        mapInstance.on('mouseleave', 'unclustered-cameras', () => {
          mapInstance.getCanvas().style.cursor = '';
        });

        // Start pulsing animation
        setupPulseAnimation(mapInstance);
      });

    } catch (error) {
      console.error('Error creating map:', error);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [onCameraClick, incidents]);

  return (
    <div style={{ position: 'absolute', top: 0, bottom: 0, width: '100%' }}>
      <div ref={mapContainer} style={{ position: 'absolute', top: 0, bottom: 0, width: '100%' }} />
    </div>
  );
});

export default Map; 